import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { formatMoney } from '../utils/formatMoney';

function defaultYearMonth() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

/** RFC-style CSV cell (quotes if needed). */
function escapeCsvCell(value) {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function formatBalanceForCsv(n) {
  const x = typeof n === 'number' ? n : parseFloat(String(n));
  return Number.isFinite(x) ? x.toFixed(2) : '';
}

/** Download current report as CSV (UTF-8 BOM for Excel). */
function downloadEomReportCsv(rows, year, month) {
  const header = ['Year', 'Month', 'File no. (Ref)', 'End of month balance'];
  const lines = [
    header,
    ...rows.map((r) => [
      year,
      month,
      r.fileNo,
      formatBalanceForCsv(r.endOfMonthBalance),
    ]),
  ];
  const body = lines.map((line) => line.map(escapeCsvCell).join(',')).join('\r\n');
  const csv = `\uFEFF${body}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `month-end-report-${year}-${String(month).padStart(2, '0')}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const EomReport = () => {
  const [{ year, month }, setYearMonth] = useState(defaultYearMonth);
  const [rows, setRows] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${API_URL}/api/ledger/end-of-month-report`,
        { params: { year, month } }
      );
      setRows(response.data.rows || []);
      setGeneratedAt(response.data.generatedAt || null);
    } catch (err) {
      setRows([]);
      setGeneratedAt(null);
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const monthLabel = new Date(year, month - 1, 1).toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 12 }, (_, i) => currentYear - 6 + i);

  return (
    <div>
      <div className="card">
        <h2 style={{ marginBottom: '12px' }}>End of month report</h2>
        <p className="text-muted" style={{ marginBottom: '20px', fontSize: '14px' }}>
          Ledgers with a non-zero balance at the end of the selected month (running
          total of all entries on or before that month). Zero-balance ledgers are not
          listed. Use <strong>Download CSV</strong> to export the current results for
          the selected period.
        </p>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="filter-group" style={{ marginBottom: '20px' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '140px' }}>
            <label htmlFor="eom-year">Year</label>
            <select
              id="eom-year"
              value={year}
              onChange={(e) =>
                setYearMonth((s) => ({ ...s, year: parseInt(e.target.value, 10) }))
              }
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '140px' }}>
            <label htmlFor="eom-month">Month</label>
            <select
              id="eom-month"
              value={month}
              onChange={(e) =>
                setYearMonth((s) => ({ ...s, month: parseInt(e.target.value, 10) }))
              }
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          <div
            style={{
              alignSelf: 'flex-end',
              marginBottom: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <button
              type="button"
              className="btn btn-neutral-outline"
              onClick={() => downloadEomReportCsv(rows, year, month)}
              disabled={loading || rows.length === 0}
            >
              Download CSV
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={fetchReport}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {generatedAt && (
          <p className="text-muted" style={{ fontSize: '13px', marginBottom: '16px' }}>
            As of {new Date(generatedAt).toLocaleString()} — <strong>{monthLabel}</strong>{' '}
            ({rows.length} ledger{rows.length === 1 ? '' : 's'})
          </p>
        )}
      </div>

      {loading && !rows.length && !error && (
        <div className="loading">Loading report…</div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="card">
          <p className="text-muted" style={{ textAlign: 'center', padding: '24px' }}>
            No ledgers with a non-zero balance at the end of {monthLabel}.
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="card">
          <h3 style={{ marginBottom: '16px' }}>Ledgers ({rows.length})</h3>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>File no. (Ref)</th>
                  <th>End of month balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.fileNo}>
                    <td style={{ fontWeight: 600 }}>{row.fileNo}</td>
                    <td
                      className={
                        row.endOfMonthBalance >= 0 ? 'amount-positive' : 'amount-negative'
                      }
                    >
                      {formatMoney(row.endOfMonthBalance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default EomReport;
